import React from 'react';

import { CardPaymentsTableSortableField, CardPayment } from 'types/state';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import {
  TableTr,
  TableWrapper,
  TableHead,
  TableTh,
  TableBody,
} from 'design/table';
import { CardPaymentsTableRow } from 'containers/CardPaymentsTable';

type Props = {
  payments: CardPayment[];
  sortedBy: CardPaymentsTableSortableField;
  ascendingSort: boolean;
  activeItemId?: number;
  onSortChange(nextSortBy: CardPaymentsTableSortableField): void;
  onItemClick(item: number): void;
};

export const CardPaymentsTable = (props: Props) => {
  const {
    payments,
    sortedBy,
    ascendingSort,
    activeItemId,
    onSortChange,
    onItemClick,
  } = props;

  const { cardPaymentsTableColumns: columnsIntl } = useIntlDictionary();

  const onIdClick = React.useCallback(() => {
    onSortChange('id');
  }, [onSortChange]);

  const onOrganizationClick = React.useCallback(() => {
    onSortChange('organization');
  }, [onSortChange]);

  const onFullNameClick = React.useCallback(() => {
    onSortChange('fullName');
  }, [onSortChange]);

  const onRecievedAmountClick = React.useCallback(() => {
    onSortChange('recievedAmount');
  }, [onSortChange]);

  const onCreditedAmountClick = React.useCallback(() => {
    onSortChange('creditedAmount');
  }, [onSortChange]);

  const onStatusClick = React.useCallback(() => {
    onSortChange('status');
  }, [onSortChange]);

  const onUpdatedAtClick = React.useCallback(() => {
    onSortChange('updatedAt');
  }, [onSortChange]);

  const tableRowsNode = React.useMemo(() => {
    return payments.map((payment) => (
      <CardPaymentsTableRow
        key={payment.id}
        active={activeItemId === payment.id}
        payment={payment}
        onClick={onItemClick}
      />
    ));
  }, [payments, activeItemId, onItemClick]);

  return (
    <TableWrapper>
      <TableHead>
        <TableTr>
          <TableTh
            size="xs"
            sorted={sortedBy === 'id'}
            ascending={ascendingSort}
            text={columnsIntl.id}
            onClick={onIdClick}
          />
          <TableTh
            size="auto"
            sorted={sortedBy === 'organization'}
            ascending={ascendingSort}
            text={columnsIntl.organization}
            onClick={onOrganizationClick}
          />
          <TableTh
            size="xxl"
            sorted={sortedBy === 'fullName'}
            ascending={ascendingSort}
            text={columnsIntl.fullName}
            onClick={onFullNameClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'recievedAmount'}
            ascending={ascendingSort}
            text={columnsIntl.recievedAmount}
            onClick={onRecievedAmountClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'creditedAmount'}
            ascending={ascendingSort}
            text={columnsIntl.creditedAmount}
            onClick={onCreditedAmountClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'status'}
            ascending={ascendingSort}
            text={columnsIntl.status}
            onClick={onStatusClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'updatedAt'}
            ascending={ascendingSort}
            text={columnsIntl.updatedAt}
            onClick={onUpdatedAtClick}
          />
        </TableTr>
      </TableHead>
      <TableBody>{tableRowsNode}</TableBody>
    </TableWrapper>
  );
};
